import styled from "styled-components/macro";
import {
  trendChartTargetLineColor,
  qualityLossColor,
  untaggedColor,
  plannedColor,
  unplannedColor,
  noProductionScheduledColor,
} from "../../../../constants/colors";
import { TREND_CHART_TARGET_STROKE_DASHARRAY } from "../TrendChartContent/TrendChartAxis";
import { TREND_CHART_CONFIG } from "constants/customers";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

const DASH_LINE_HEIGHT = 4;
const DASH_LINE_WIDTH = 60;
const CIRCLE_WIDTH = 20;
const CIRCLE_MARGIN_SIDES = 6;

export const DEFAULT_LEGEND_CATEGORIES = [
  {
    name: "quality loss",
    color: qualityLossColor,
  },
  {
    name: "unknown loss",
    color: untaggedColor,
  },
  {
    name: "changeover",
    color: plannedColor,
  },
  {
    name: "packaging loss",
    color: unplannedColor,
  },
  {
    name: "closure loss",
    color: noProductionScheduledColor,
  },
];

const LegendStyles = styled.div<{ $scale: number }>`
  display: flex;
  padding: ${props => props.$scale}em;
  font-size: ${props => Math.max(0.75, props.$scale)}em;
  .column {
    display: flex;
    flex-direction: column;
    &:not(:last-child) {
      margin-right: ${props => props.$scale}em;
    }
    &:last-child {
      margin-left: ${props => props.$scale * 5}em;
    }
  }
  .item {
    display: flex;
    align-items: center;
    .colorSquare {
      min-width: ${props => props.$scale * CIRCLE_WIDTH}px;
      min-height: ${props => props.$scale * CIRCLE_WIDTH}px;
      margin: ${props => props.$scale * 3}px
        ${props => props.$scale * CIRCLE_MARGIN_SIDES}px;
      &.dashedLine {
        display: flex;
        align-items: center;
        width: ${props => props.$scale * DASH_LINE_WIDTH}px;
        margin-left: -${props => props.$scale * (DASH_LINE_WIDTH - CIRCLE_WIDTH - CIRCLE_MARGIN_SIDES)}px;
        svg {
          width: 100%;
          height: ${props => props.$scale * DASH_LINE_HEIGHT}px;
        }
      }
    }
  }
`;
export default function TrendChartLegend({ scale = 1 }: { scale?: number }) {
  const [flightConfig] = useFlightConfig();
  const showTrendLine = flightConfig.trendChart.showTrendLine;
  const ckey = useCustomerKey();
  const { translate } = useFlightTranslation();

  const legendCategories =
    TREND_CHART_CONFIG[ckey]?.categories || DEFAULT_LEGEND_CATEGORIES;
  const MAX_ROWS = 2;
  return (
    <LegendStyles $scale={scale}>
      {
        // slice legendCategories into columns with a number of `rows <= MAX_ROWS`
        legendCategories.map((lc, idx) => {
          const shouldStartNewColumn = idx % MAX_ROWS === 0;
          if (shouldStartNewColumn) {
            return (
              <div className="column" key={lc.name}>
                {legendCategories
                  .slice(idx, idx + MAX_ROWS)
                  .map(({ name, color }) => (
                    <div className="item" key={name}>
                      <div
                        className="colorSquare"
                        style={{ background: color }}
                      />
                      <div className="category">{translate(name)}</div>
                    </div>
                  ))}
              </div>
            );
          } else {
            return null;
          }
        })
      }

      <div className="column">
        {(flightConfig.trendChart.target ??
          TREND_CHART_CONFIG[ckey]?.target) && (
          <div className="item">
            <div className="colorSquare dashedLine">
              <svg>
                <line
                  y1={DASH_LINE_HEIGHT / 2}
                  y2={DASH_LINE_HEIGHT / 2}
                  x1="0"
                  x2={DASH_LINE_WIDTH * scale}
                  strokeWidth="4"
                  strokeDasharray={TREND_CHART_TARGET_STROKE_DASHARRAY * scale}
                  stroke={trendChartTargetLineColor}
                />
              </svg>
            </div>
            <div className="category">{translate("target_downtime")}</div>
          </div>
        )}
        {showTrendLine && (
          <div className="item">
            <div
              className="colorSquare"
              style={{ background: "black", borderRadius: "100%" }}
            />
            <div className="category">
              {translate("corrected_cumulative_throughput")}
            </div>
          </div>
        )}
      </div>
    </LegendStyles>
  );
}
